import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Sun, Moon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { LanguageSelector } from '../components/LanguageSelector';

const loginSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const { t } = useTranslation(['common']);
  const { user, login, error, isLoading } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  if (user) {
    return <Navigate to="/" replace />;
  }

  const onSubmit = async (data: LoginFormData) => {
    await login(data.username, data.password);
  };

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center text-foreground-muted">Loading...</div>;
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background relative">
      <div className="absolute top-5 right-5 flex items-center gap-3">
        <LanguageSelector />
        <button
          className="flex items-center justify-center w-9 h-9 rounded-lg border border-border bg-surface text-foreground-muted cursor-pointer transition-all hover:bg-surface-hover hover:text-foreground hover:border-foreground-muted"
          onClick={toggleTheme}
          title={theme === 'light' ? t('theme.dark') : t('theme.light')}
        >
          {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
        </button>
      </div>
      <div className="w-full max-w-[400px] p-10 bg-surface rounded-lg shadow-md border border-border">
        <h1 className="text-[28px] font-bold text-center mb-2 text-foreground">{t('app.name')}</h1>
        <p className="text-center text-foreground-muted mb-8">{t('login.subtitle')}</p>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
          {error && (
            <div className="p-3 bg-amount-out-bg border border-error rounded mb-4 text-error text-sm">
              {error}
            </div>
          )}

          <div className="form-group">
            <label className="form-label" htmlFor="username">
              {t('login.username')}
            </label>
            <input
              id="username"
              type="text"
              className={`form-input ${errors.username ? 'border-error focus:border-error focus:ring-error/20' : ''}`}
              {...register('username')}
              autoComplete="username"
              autoFocus
            />
            {errors.username && (
              <span className="block text-error text-[13px] mt-1">{errors.username.message}</span>
            )}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="password">
              {t('login.password')}
            </label>
            <div className="relative flex items-center">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className={`form-input !pr-11 ${errors.password ? 'border-error focus:border-error focus:ring-error/20' : ''}`}
                {...register('password')}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="absolute right-2 bg-transparent border-none p-1 cursor-pointer text-foreground-muted flex items-center justify-center rounded transition-colors hover:text-foreground"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && (
              <span className="block text-error text-[13px] mt-1">{errors.password.message}</span>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-primary mt-2 py-3 text-base"
            disabled={isSubmitting}
          >
            {isSubmitting ? t('login.signingIn') : t('login.signIn')}
          </button>
        </form>
      </div>
      <div className="mt-4 text-foreground-muted text-sm">v{__APP_VERSION__}</div>
    </div>
  );
}
